import { computed, ref } from 'vue';
import { ValueOf } from '@/types/utils';

export const TRANSITIONS = {
    FADE: 'fade',
    SLIDE_LEFT: 'slide-left',
    SLIDE_RIGHT: 'slide-right',
} as const;

export type TransitionName = ValueOf<typeof TRANSITIONS>;

export interface UseTransitionOptions {
    enterClass?: string;
    activeClass?: string;
    leaveToClass?: string;
}

type TransitionClassMap = Partial<Record<TransitionName, string>>;

function joinClasses(...classes: Array<string | undefined>) {
    return classes.filter(Boolean).join(' ');
}

function getTransitionClass(name: TransitionName | undefined, classes: TransitionClassMap) {
    if (!name) return '';

    return classes[name] ?? '';
}

export default function useTransition(options: UseTransitionOptions = {}) {
    const name = ref<TransitionName>();

    function change(newName?: TransitionName) {
        name.value = newName;
    }

    function reset() {
        change();
    }

    const enterClass = computed(() => joinClasses(options.enterClass, getTransitionClass(name.value, {
        [TRANSITIONS.SLIDE_LEFT]: 'translate-x-full',
        [TRANSITIONS.SLIDE_RIGHT]: '-translate-x-full',
        [TRANSITIONS.FADE]: 'opacity-0',
    })));

    const leaveToClass = computed(() => joinClasses(options.leaveToClass, getTransitionClass(name.value, {
        [TRANSITIONS.SLIDE_LEFT]: '-translate-x-full',
        [TRANSITIONS.SLIDE_RIGHT]: 'translate-x-full',
        [TRANSITIONS.FADE]: 'opacity-0',
    })));

    const activeClass = computed(() => joinClasses(options.activeClass, getTransitionClass(name.value, {
        [TRANSITIONS.SLIDE_LEFT]: 'overflow-hidden duration-500',
        [TRANSITIONS.SLIDE_RIGHT]: 'overflow-hidden duration-500',
        [TRANSITIONS.FADE]: 'duration-300',
    })));

    const transitionMode = computed(() => (name.value ? '' : 'out-in'));

    return {
        activeClass,
        enterClass,
        leaveToClass,

        transitionMode,

        name,
        change,
        reset,
    };
}
